import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { BugReport, BugSeverity, BugStats, BugStatus } from '@/types';

export interface BugFilters {
  status?: BugStatus[];
  severity?: BugSeverity[];
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export function useBugReports(filters?: BugFilters) {
  return useQuery({
    queryKey: ['bugs', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status?.length) params.append('status', filters.status.join(','));
      if (filters?.severity?.length) params.append('severity', filters.severity.join(','));
      if (filters?.search) params.append('search', filters.search);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.sortBy) params.append('sortBy', filters.sortBy);
      if (filters?.sortDir) params.append('sortDir', filters.sortDir);

      const { data } = await api.get<PaginatedResponse<BugReport>>(`/bugs?${params.toString()}`);
      return data;
    },
  });
}

export function useBugReport(id?: string) {
  return useQuery({
    queryKey: ['bugs', id],
    queryFn: async () => {
      const { data } = await api.get<BugReport>(`/bugs/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useCreateBugReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      title: string;
      description: string;
      stepsToReproduce?: string;
      expectedBehaviour?: string;
      actualBehaviour?: string;
      severity: BugSeverity;
      pageUrl?: string;
      browserInfo?: string;
    }) => {
      const { data } = await api.post<BugReport>('/bugs', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bugs'] });
    },
  });
}

export function useUpdateBugReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: Partial<BugReport> }) => {
      const { data } = await api.patch<BugReport>(`/bugs/${id}`, payload);
      return data;
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['bugs'] });
      queryClient.invalidateQueries({ queryKey: ['bugs', data.id] });
    },
  });
}

export function useBugComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      bugId,
      body,
      isInternal,
    }: {
      bugId: string;
      body: string;
      isInternal?: boolean;
    }) => {
      const { data } = await api.post<BugReport>(`/bugs/${bugId}/comment`, { body, isInternal });
      return data;
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['bugs'] });
      queryClient.invalidateQueries({ queryKey: ['bugs', data.id] });
    },
  });
}

export function useBugStats() {
  return useQuery({
    queryKey: ['bugs', 'stats'],
    queryFn: async () => {
      const { data } = await api.get<BugStats>('/bugs/stats');
      return data;
    },
  });
}

export function useMyBugReports(filters?: BugFilters) {
  return useQuery({
    queryKey: ['bugs', 'my', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status?.length) params.append('status', filters.status.join(','));
      if (filters?.severity?.length) params.append('severity', filters.severity.join(','));
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());

      const { data } = await api.get<PaginatedResponse<BugReport>>(`/bugs/my?${params.toString()}`);
      return data;
    },
  });
}
